import ConfigHelper from 'framework/helpers/config'
import { of } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import Fetch from '../fetch'

const PARENTAL_CONTROL_SETTING = 'PARENTAL_CONTROL_SETTING'
export default class SettingsApi extends Fetch {
    constructor(config, otherApis = {}) {
        super(config, otherApis)
        this.pinApi = otherApis.pinApi
        this.userPreferencesApi = otherApis.userPreferencesApi
    }

    _defaultPcLevel = ConfigHelper.getInstance().getConfig('defaultUserPcLevel')
    /**
     * Updates the Parental Control level of the user
     *
     * IMPORTANT
     *
     * Nagra has no any separate API for working with parental control (PC) settings. So the trick
     * is that we are using BookmarksApi for applying PC to the user.
     *
     * BookmarkApi uses `tags` for filtering result and `metadata` to store additional information.
     * So for PC we always create some bookmark with `PARENTAL_CONTROL_SETTING` in tags for filtering
     * and `{ pcLevel: level }` for storing the value of PC itself.
     *
     * @param {String|Number} level
     * @return {Observable}
     */
    updatePcLevel(level) {
        return this.userPreferencesApi
            .addBookmark(
                0,
                PARENTAL_CONTROL_SETTING,
                PARENTAL_CONTROL_SETTING,
                { pcLevel: level },
                [PARENTAL_CONTROL_SETTING]
            )
            .pipe(mergeMap(() => of(true)))
    }

    /**
     * Gets the user's parental control (PC) level value
     *
     * IMPORTANT
     *
     * Nagra has no any separate API for working with parental control (PC) settings. So the trick
     * is that we are using BookmarksApi for applying PC to the user.
     *
     * This function requests BookmarkApi with specific tag `PARENTAL_CONTROL_SETTING` for filtering.
     *
     * @returns {Number} return PC level for the user
     */
    getPcLevel() {
        return this.userPreferencesApi.getBookmarks('PARENTAL_CONTROL_SETTING').pipe(
            mergeMap((bookmarks = []) => {
                if (bookmarks.length && bookmarks[0].metadata && bookmarks[0].metadata.pcLevel) {
                    return of(+bookmarks[0].metadata.pcLevel)
                }
                return of(this._defaultPcLevel)
            }),
            catchError(() => {
                this.logger.error(
                    'Error while getting parental control level. Returning default value...'
                )
                return of(this._defaultPcLevel)
            })
        )
    }

    /**
     * Updates the user's PIN code
     * @param {PinApi.TYPE} type - type of PIN to update, either PURCHASE, PARENTAL or CENSORSHIP (cf PinApi.TYPE)
     * @param {String} newPin - new PIN to connect with a user
     * @param {String} oldPin - current user's PIN
     * @return {Observable<Boolean>} returns true in case of success
     */
    updatePin(type, newPin, oldPin) {
        return this.pinApi.updatePin(type, newPin, oldPin)
    }

    /**
     * Modify user password
     * @param {Object} arg
     * @param {String} arg.password Current Password
     * @param {String} arg.newPassword New Password
     * @return {Observable<Boolean>} true if the password is verified
     */
    updatePassword({ password, newPassword }) {
        return of(true)
    }

    /**
     * Checks if a given password is valid (can be used before performing specific action like Reset PIN)
     * @param {Object} arg
     * @param {String} arg.password Password
     * @return {Observable<Boolean>} true if the password is verified
     */
    verifyPassword({ password }) {
        return of(true)
    }

    /**
     * Checks if a given password is valid (can be used before performing specific action like Reset PIN)
     * @param {Object} arg
     * @param {String} arg.languageCode Should be two digit
     * @return {Observable<Boolean>} true if the password is verified
     */
    updateAccountLocale({ languageCode }) {
        return of(true)
    }
}
